import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Gift, KeyRound, ShieldCheck, Copy, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';

const LoginPage = () => {
  const [params] = useSearchParams();
  const refFromUrl = (params.get('ref') || '').toUpperCase();
  const [mode, setMode] = useState(refFromUrl ? 'signup' : 'login');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [name, setName] = useState('');
  const [referralCode, setReferralCode] = useState(refFromUrl);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // Forgot password state
  const [resetStep, setResetStep] = useState('request'); // 'request' | 'redeem'
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);

  const nav = useNavigate();
  const { toast } = useToast();
  const { login, register } = useAuth();

  useEffect(() => { if (refFromUrl) setReferralCode(refFromUrl); }, [refFromUrl]);

  const goto = (newMode) => {
    setMode(newMode);
    setResetStep('request');
    setResetCode(''); setNewPassword(''); setConfirmPw('');
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    if (!email || !pw) { toast({ title: 'Missing fields', description: 'Please enter email and password.' }); return; }
    if (mode === 'signup' && !name) { toast({ title: 'Missing name', description: 'Please enter your full name.' }); return; }
    setLoading(true);
    try {
      const user = mode === 'login'
        ? await login(email, pw)
        : await register(email, pw, name, referralCode.trim() || null);
      toast({ title: mode === 'login' ? 'Welcome back' : 'Account created', description: `Signed in as ${user.email}` });
      nav('/dashboard');
    } catch (err) {
      toast({ title: 'Auth failed', description: err?.response?.data?.detail || err?.message || 'Something went wrong' });
    } finally { setLoading(false); }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    if (!email) { toast({ title: 'Enter your email' }); return; }
    setLoading(true);
    try {
      const res = await authAPI.forgotPassword(email.trim());
      if (res.reset_code) {
        setResetCode(res.reset_code);
        setResetStep('redeem');
        toast({ title: 'Reset code generated', description: 'Copy the code below and set a new password.' });
      } else {
        toast({ title: 'Check your email', description: res.message || 'If that email exists, a reset code has been sent.' });
      }
    } catch (err) {
      toast({ title: 'Request failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetCode || !newPassword) { toast({ title: 'Missing fields', description: 'Enter the reset code and new password.' }); return; }
    if (newPassword.length < 6) { toast({ title: 'Password too short', description: 'Use at least 6 characters.' }); return; }
    if (newPassword !== confirmPw) { toast({ title: 'Passwords do not match' }); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword(resetCode.trim(), newPassword);
      toast({ title: 'Password updated', description: 'You can now sign in with your new password.' });
      setPw('');
      goto('login');
    } catch (err) {
      toast({ title: 'Reset failed', description: err?.response?.data?.detail || 'Invalid or expired code' });
    } finally { setLoading(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(resetCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1400);
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050807] hero-glow flex items-center justify-center px-4 sm:px-6 py-10 sm:py-16 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40"/>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M6 22 L18 6 L26 12 L14 28 Z" fill="#00e676"/>
            <path d="M14 28 L22 20" stroke="#062516" strokeWidth="2"/>
          </svg>
          <span className="text-2xl font-extrabold text-white whitespace-nowrap">NEXUS FX <span className="text-[#00e676]">PRO</span></span>
        </Link>
        <div className="bg-[#0a1210] border border-[#1a2823] rounded-2xl p-6 sm:p-8 shadow-2xl">
          {mode !== 'forgot' && (
            <>
              <div className="flex gap-2 p-1 bg-[#050807] rounded-lg mb-6">
                <button onClick={() => goto('login')} data-testid="tab-login-btn"
                        className={`flex-1 py-2 text-sm font-semibold rounded-md transition ${mode === 'login' ? 'bg-[#00e676] text-black' : 'text-gray-400 hover:text-white'}`}>Sign In</button>
                <button onClick={() => goto('signup')} data-testid="tab-signup-btn"
                        className={`flex-1 py-2 text-sm font-semibold rounded-md transition ${mode === 'signup' ? 'bg-[#00e676] text-black' : 'text-gray-400 hover:text-white'}`}>Register</button>
              </div>
              <h2 className="text-xl font-bold text-white">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h2>
              <p className="text-sm text-gray-400 mt-1">{mode === 'login' ? 'Access your NEXUS FX PRO trading dashboard.' : 'Start trading in minutes.'}</p>

              <button
                type="button"
                onClick={googleSignIn}
                data-testid="google-signin-btn"
                className="mt-6 w-full flex items-center justify-center gap-2.5 py-3 rounded-lg bg-white text-[#1f1f1f] font-semibold text-sm hover:bg-gray-100 transition"
              >
                <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
                Continue with Google
              </button>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-[#1a2823]"/>
                <span className="text-[11px] text-gray-500 uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-[#1a2823]"/>
              </div>

              <form onSubmit={submitAuth} className="mt-6 space-y-4">
                {mode === 'signup' && (
                  <div>
                    <label className="text-xs text-gray-400 font-medium">Full name</label>
                    <input value={name} onChange={e => setName(e.target.value)} placeholder="Alex Trader" className="mt-1.5 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00e676] transition"/>
                  </div>
                )}
                {mode === 'signup' && (
                  <div>
                    <label className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                      <Gift size={12} className="text-[#00e676]"/> Referral code <span className="text-gray-600">(optional)</span>
                    </label>
                    <input
                      data-testid="signup-referral-input"
                      value={referralCode}
                      onChange={e => setReferralCode(e.target.value.toUpperCase())}
                      placeholder="e.g. NEXUS123"
                      className={`mt-1.5 w-full bg-[#050807] border rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00e676] transition mono tracking-wider ${referralCode ? 'border-[#00e676]/40' : 'border-[#1a2823]'}`}
                    />
                    {refFromUrl && (
                      <div className="mt-1.5 text-[11px] text-[#00e676] flex items-center gap-1">
                        <Gift size={11}/> You were invited — a friend will earn commission on your deposits.
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-400 font-medium">Email</label>
                  <div className="relative mt-1.5">
                    <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"/>
                    <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@email.com"
                           className="w-full bg-[#050807] border border-[#1a2823] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#00e676] transition"/>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-gray-400 font-medium">Password</label>
                    {mode === 'login' && (
                      <button type="button" onClick={() => goto('forgot')} data-testid="forgot-password-link"
                              className="text-[11px] text-[#00e676] hover:underline font-semibold">Forgot password?</button>
                    )}
                  </div>
                  <div className="relative mt-1.5">
                    <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"/>
                    <input value={pw} onChange={e => setPw(e.target.value)} type={showPw ? 'text' : 'password'} placeholder="••••••••"
                           className="w-full bg-[#050807] border border-[#1a2823] rounded-lg pl-10 pr-11 py-3 text-white text-sm focus:outline-none focus:border-[#00e676] transition"/>
                    <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                      {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="submit-auth-btn"
                        className="btn-green w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-70">
                  {loading ? 'Please wait...' : (mode === 'login' ? 'Sign In' : 'Create Account')} <ArrowRight size={16}/>
                </button>
              </form>
            </>
          )}

          {mode === 'forgot' && (
            <div data-testid="forgot-password-panel">
              <button type="button" onClick={() => goto('login')} className="text-xs text-gray-400 hover:text-[#00e676] mb-4">← Back to sign in</button>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <KeyRound size={18} className="text-[#00e676]"/> Reset your password
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {resetStep === 'request'
                  ? 'Enter your account email and we\u2019ll issue a one-time reset code (valid 30 min).'
                  : 'We generated a reset code. Enter a new password below to complete the reset.'}
              </p>

              {resetStep === 'request' && (
                <form onSubmit={requestReset} className="mt-6 space-y-4">
                  <div>
                    <label className="text-xs text-gray-400 font-medium">Email</label>
                    <div className="relative mt-1.5">
                      <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"/>
                      <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@email.com"
                             data-testid="forgot-email-input"
                             className="w-full bg-[#050807] border border-[#1a2823] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#00e676] transition"/>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} data-testid="send-reset-code-btn"
                          className="btn-green w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-70">
                    {loading ? 'Sending...' : 'Send reset code'} <ArrowRight size={16}/>
                  </button>
                  <div className="text-[11px] text-gray-500 text-center">
                    Already have a code? <button type="button" onClick={() => setResetStep('redeem')} className="text-[#00e676] hover:underline">Enter it manually</button>
                  </div>
                </form>
              )}

              {resetStep === 'redeem' && (
                <form onSubmit={submitReset} className="mt-6 space-y-4">
                  {resetCode && (
                    <div className="p-3 rounded-lg bg-[#0a1a13] border border-[#00e676]/30" data-testid="reset-code-display">
                      <div className="text-[10px] uppercase tracking-widest text-[#00e676] font-semibold flex items-center gap-1 mb-1.5">
                        <ShieldCheck size={11}/> Your reset code (demo mode)
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs text-white mono break-all">{resetCode}</code>
                        <button type="button" onClick={copyCode}
                                className="btn-outline-green px-2 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1">
                          {codeCopied ? <><Check size={11}/> Copied</> : <><Copy size={11}/> Copy</>}
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1.5">In production this code would be emailed to you and never shown on screen.</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-gray-400 font-medium">Reset code</label>
                    <div className="relative mt-1.5">
                      <KeyRound size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"/>
                      <input value={resetCode} onChange={e => setResetCode(e.target.value)} placeholder="Paste the reset code"
                             data-testid="reset-code-input"
                             className="w-full bg-[#050807] border border-[#1a2823] rounded-lg pl-10 pr-4 py-3 text-white text-sm mono focus:outline-none focus:border-[#00e676] transition"/>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-medium">New password</label>
                    <div className="relative mt-1.5">
                      <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"/>
                      <input value={newPassword} onChange={e => setNewPassword(e.target.value)} type={showPw ? 'text' : 'password'} placeholder="At least 6 characters"
                             data-testid="new-password-input"
                             className="w-full bg-[#050807] border border-[#1a2823] rounded-lg pl-10 pr-11 py-3 text-white text-sm focus:outline-none focus:border-[#00e676] transition"/>
                      <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                        {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-medium">Confirm password</label>
                    <div className="relative mt-1.5">
                      <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"/>
                      <input value={confirmPw} onChange={e => setConfirmPw(e.target.value)} type={showPw ? 'text' : 'password'} placeholder="Re-enter your new password"
                             data-testid="confirm-password-input"
                             className="w-full bg-[#050807] border border-[#1a2823] rounded-lg pl-10 pr-11 py-3 text-white text-sm focus:outline-none focus:border-[#00e676] transition"/>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} data-testid="submit-reset-btn"
                          className="btn-green w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-70">
                    {loading ? 'Updating...' : 'Update password'} <ArrowRight size={16}/>
                  </button>
                </form>
              )}
            </div>
          )}

          <div className="mt-4 text-center text-xs text-gray-500">
            By continuing, you agree to the <a href="#" className="text-[#00e676]">Terms</a> and <a href="#" className="text-[#00e676]">Privacy Policy</a>.
          </div>
        </div>
        <div className="text-center mt-6 text-xs text-gray-500">
          <Link to="/" className="hover:text-[#00e676]">← Back to homepage</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
